/**
 * 🚨 [Phase 1.b Hotfix Sécurité — 2026-07-18] Composant PageGuard.
 *
 * Wrapper à appliquer sur CHAQUE route sensible dans `App.js`. Bloque le
 * rendu de la page si les permissions ne sont pas remplies. Fail-secure :
 * ne rend JAMAIS `children` tant que les permissions ne sont pas connues.
 *
 * Principe defense-in-depth :
 *   • Frontend = polish UX (spinner + redirect propre + toast)
 *   • Backend = source unique de vérité (403 même si le frontend est contourné)
 *
 * Ordre de check (STRICT, fail-secure) :
 *   1. `authLoading` OU `!user` → spinner (jamais de contenu leaked pendant l'auth)
 *   2. `estProprietaire(user)` → short-circuit → children (Owner passe toujours)
 *   3. `permsLoading` OU `!permissionsLoaded` → spinner (race condition fix)
 *   4. `canSeePage(pageKey) === false` → Navigate redirect + toast d'erreur
 *   5. Sinon → children
 *
 * Rétrocompat : les routes non-sensibles (Mon espace, Pointage, Messagerie, etc.)
 * ne doivent PAS être enveloppées.
 */
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { toast } from 'sonner';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const ROLES_TERRAIN = new Set(['chef_equipe', 'peintre', 'installateur', 'terrain']);

function FullPageSpinner({ testid = 'page-guard-spinner' }) {
  return (
    <div
      className="min-h-[50vh] flex items-center justify-center"
      data-testid={testid}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Vérification des accès…</p>
      </div>
    </div>
  );
}

export function PageGuard({ pageKey, children }) {
  const { user, loading: authLoading } = useAuth();
  const { canSeePage, loading: permsLoading, permissionsLoaded, degradedMode } = usePermissions();
  const location = useLocation();

  // ─── Effet : toast d'erreur au refus (isolé pour ne pas polluer le render)
  const authOk = !authLoading && !!user;
  const isOwner = estProprietaire(user);
  const permsReady = isOwner || (!permsLoading && permissionsLoaded);
  const allowed = isOwner || (permsReady && canSeePage(pageKey));

  useEffect(() => {
    if (authOk && !isOwner && permsReady && !allowed) {
      const msg = degradedMode
        ? 'Impossible de vérifier vos permissions — accès refusé par sécurité.'
        : 'Vous n\'avez pas la permission d\'accéder à cette page.';
      toast.error(msg);
    }
  }, [authOk, isOwner, permsReady, allowed, degradedMode]);

  // 1. Auth pas encore résolu → spinner (JAMAIS de contenu leaked)
  if (authLoading) {
    return <FullPageSpinner testid="page-guard-spinner-auth" />;
  }
  if (!user) {
    // AuthContext gère déjà la redirection vers /login au niveau supérieur.
    // Ici on ne rend rien pour éviter tout flash.
    return <FullPageSpinner testid="page-guard-spinner-no-user" />;
  }

  // 2. Owner → short-circuit
  if (isOwner) {
    return children;
  }

  // 🚑 [v13.62 BLOC 1.1 sous-problème — 2026-02-26] EXCEPTION KIOSQUE ENTREPÔT.
  //   La session kiosque du poste dédié (déjà protégée par login ID/ID via
  //   `/kiosk/entrepot` + JWT court `scope='entrepot'`) donne accès à l'interface
  //   Entrepôt PEU IMPORTE le rôle de l'employé connecté — c'est l'ancien
  //   comportement voulu (avant l'ajout de PageGuard en Phase 1.b Hotfix
  //   Sécurité 2026-07-18). Rationale : le contexte physique de session dédiée
  //   ≠ permission de rôle. Un peintre sur son téléphone reste bloqué si
  //   `pages.entrepot=False` (matrice = SoT pour sessions normales).
  //
  //   Périmètre STRICT (défense-en-profondeur) :
  //     • `pageKey === 'entrepot'` (aucune autre page)
  //     • `localStorage.kiosk_mode === 'entrepot'` (flag préservé au logout kiosque)
  //   Backend inchangé : les endpoints inventory acceptent déjà le token JWT
  //   kiosque via `Depends(get_current_user)` (validé : aucun `require_owner`
  //   ne bloque le use-case kiosque — lecture + sortie/retour matériel).
  //
  //   Doc : REGLES_PLATEFORME.md §Exceptions RBAC approuvées + RCA_V13_62_REGRESSIONS_RBAC.md.
  if (
    pageKey === 'entrepot'
    && typeof window !== 'undefined'
    && window.localStorage
    && window.localStorage.getItem('kiosk_mode') === 'entrepot'
  ) {
    return children;
  }

  // 3. Permissions pas encore chargées → spinner (fail-secure race condition)
  if (!permsReady) {
    return <FullPageSpinner testid="page-guard-spinner-perms" />;
  }

  // 4. Refus → redirect
  if (!allowed) {
    // Rôle terrain → /mon-espace, autres (charge_projet, adjoint_admin) → /
    const fallback = ROLES_TERRAIN.has(user.role) ? '/mon-espace' : '/';
    // Si on est DÉJÀ sur la route fallback, ne pas boucler infiniment
    if (location.pathname === fallback) {
      return <FullPageSpinner testid="page-guard-spinner-loop-safe" />;
    }
    return <Navigate to={fallback} replace state={{ from: location.pathname, deniedPage: pageKey }} />;
  }

  // 5. Autorisé
  return children;
}
